<script setup lang="ts">
/**
 * Presentational list. Receives rows + pagination + filter-count via
 * props; emits update:page / update:pageSize / filter-click /
 * view-transactions. No queries, no mutations, no router pushes —
 * orchestration lives in the container.
 *
 * Row kebab exposes a single "View transactions" action that deep-links
 * to the fiat-transactions list pre-filtered to this investor's
 * transactions.
 */
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import DataTableV2, { type TableColumn, type TableAction } from '@uiKit/components/DataTableV2.vue'
import { formatDate, formatMoney } from '@shared/utils'
import type { WalletDirectoryRow } from '../types/walletsDirectoryTypes'

defineProps<{
  rows: WalletDirectoryRow[]
  loading: boolean
  total: number
  page: number
  pageSize: number
  filterCount: number
  emptyTitle?: string
  emptySubtitle?: string
  title?: string
  subtitle?: string
  search?: string
}>()

const emit = defineEmits<{
  (e: 'update:page', value: number): void
  (e: 'update:pageSize', value: number): void
  (e: 'filter-click'): void
  (e: 'view-transactions', row: WalletDirectoryRow): void
  (e: 'update:search', value: string): void
}>()

const columns: TableColumn[] = [
  { key: 'investor', label: 'INVESTOR', width: 'minmax(240px, 1.6fr)' },
  { key: 'totalBalance', label: 'TOTAL', width: 'minmax(140px, 1fr)', align: 'right' },
  { key: 'availableBalance', label: 'AVAILABLE', width: 'minmax(140px, 1fr)', align: 'right' },
  { key: 'lockedBalance', label: 'LOCKED', width: 'minmax(140px, 1fr)', align: 'right' },
  { key: 'updatedAt', label: 'LAST UPDATED', width: 'minmax(160px, 1fr)' },
]

const rowActions: TableAction<WalletDirectoryRow>[] = [
  {
    label: 'View transactions',
    icon: 'arrow-right',
    onClick: (row) => emit('view-transactions', row),
  },
]

function formatBalance(currency: string, amount: string): string {
  return formatMoney(currency, amount)
}
</script>

<template>
  <DataTableV2
    :columns="columns"
    :rows="rows"
    :loading="loading"
    row-key="id"
    :title="title"
    :subtitle="subtitle"
    :search="search"
    search-placeholder="Search by investor name…"
    :local-search="false"
    :local-pagination="false"
    :filterable="true"
    :filter-count="filterCount"
    :page="page"
    :page-size="pageSize"
    :total="total"
    :url-sync="true"
    page-size-storage-key="treasury.walletsDirectory.pageSize"
    :actions="rowActions"
    :empty-title="emptyTitle ?? 'No investor wallets'"
    :empty-subtitle="emptySubtitle ?? 'Wallets matching your filters will appear here.'"
    @update:page="emit('update:page', $event)"
    @update:page-size="emit('update:pageSize', $event)"
    @update:search="emit('update:search', $event)"
    @filter-click="emit('filter-click')"
  >
    <template #cell-investor="{ row }">
      <Box flex direction="col">
        <Text type="body" weight="semibold">{{
          (row as WalletDirectoryRow).investorName || '—'
        }}</Text>
        <Text type="caption" color="muted">
          {{ (row as WalletDirectoryRow).investorEmail || '—' }}
        </Text>
      </Box>
    </template>

    <template #cell-totalBalance="{ row }">
      <Text
        type="body-sm"
        weight="semibold"
        align="right"
        style="font-variant-numeric: tabular-nums"
      >
        {{
          formatBalance(
            (row as WalletDirectoryRow).currencyCode,
            (row as WalletDirectoryRow).totalBalance,
          )
        }}
      </Text>
    </template>

    <template #cell-availableBalance="{ row }">
      <Text type="body-sm" color="muted" align="right" style="font-variant-numeric: tabular-nums">
        {{
          formatBalance(
            (row as WalletDirectoryRow).currencyCode,
            (row as WalletDirectoryRow).availableBalance,
          )
        }}
      </Text>
    </template>

    <template #cell-lockedBalance="{ row }">
      <Text type="body-sm" color="muted" align="right" style="font-variant-numeric: tabular-nums">
        {{
          formatBalance(
            (row as WalletDirectoryRow).currencyCode,
            (row as WalletDirectoryRow).lockedBalance,
          )
        }}
      </Text>
    </template>

    <template #cell-updatedAt="{ row }">
      <Text type="body-sm" color="muted">
        {{ formatDate((row as WalletDirectoryRow).updatedAt) }}
      </Text>
    </template>
  </DataTableV2>
</template>
